/**
 * The "best X" listicles.
 *
 * ── Why this format gets its own page family ─────────────────────────────────
 *
 * Comparison and "best X" formats are 43.8% of everything ChatGPT cites
 * (docs/SEO-GEO-RESEARCH.md, finding 1). `lib/compare` covers the first half of
 * that — one page per rival, head to head. This is the second half, and it is
 * NOT the same page with more rows: a comparison answers "which of these two",
 * a listicle answers "what are my options at all", and an assistant asked the
 * second question lifts a list, not a table.
 *
 * The two also want different addresses. Searching "fazly vs umax" returns
 * comparison pages; searching "best looksmaxxing apps" returns listicles, and
 * putting the wrong format on a keyword is the single most common way a page
 * that deserves to rank does not (docs/SEO-GEO-RESEARCH.md, finding 11).
 *
 * ── The two fields that stop this being an advertisement ─────────────────────
 *
 * A "best X" page written by one of the entries is a conflict of interest, and
 * the category is full of them: pages that list six competitors, find a fatal
 * flaw in each, and crown the author. Assistants and readers both discount that
 * shape, so the type makes the two honest moves mandatory rather than optional.
 *
 * `disclosure` is required and rendered ABOVE the list — not in a footer —
 * because a reader who finds out at the bottom that we wrote it has already
 * read the list believing something else.
 *
 * `pick` is required and is deliberately NOT always Fazly. Three of the six
 * lists recommend somebody else, because on those questions somebody else is
 * genuinely the better answer, and a list where the author wins every time is
 * a list that answered nothing. `best.test.ts` enforces the count: if a future
 * edit quietly makes us the pick everywhere, the suite fails.
 *
 * ── Why the entries carry no prices and no feature lists ─────────────────────
 *
 * Same reason as `compare/types.ts`, and the same discipline: `limitation` is a
 * statement about what a KIND of product cannot do, not a bug report about a
 * specific build that ships a fix next week. A listicle that is wrong about a
 * competitor is worse than no listicle, and it is wrong silently, for years.
 */

import type { CompareSlug } from "../compare/types";
import type { ToolSlug } from "../tools/types";

/** The lists. Slugs are the keyword, which is also the H1 and the URL. */
export const BEST_SLUGS = [
  "looksmaxxing-apps",
  "face-analysis-apps",
  "free-face-rating-sites",
  "face-shape-detectors",
  "jawline-apps",
  "mewing-apps",
] as const;

export type BestSlug = (typeof BEST_SLUGS)[number];

export interface BestEntry {
  /** The product or option, by its real name. */
  readonly name: string;
  /**
   * The reader this entry actually suits. Every entry has one — an entry
   * nobody should pick does not belong on the list.
   */
  readonly bestFor: string;
  /**
   * What this kind of option cannot do. Required, on every entry including
   * ours: an item with no limitation is an advertisement wearing a list's
   * clothes, and it is the tell that gets the whole page discounted.
   */
  readonly limitation: string;
  /**
   * Our comparison page for this product, when one exists. Typed as the slug
   * union rather than a string so a rename in `lib/compare` fails the build
   * here instead of shipping a dead internal link — orphaned and broken links
   * are the two ways a content set quietly rots.
   */
  readonly compare?: CompareSlug;
  /** Our tool page, when the entry is one of ours. Same reasoning. */
  readonly tool?: ToolSlug;
}

export interface BestCopy {
  readonly title: string;
  readonly description: string;
  readonly h1: string;
  readonly standfirst: string;
  /**
   * 134–167 words, self-contained, answer first. The block written to be
   * lifted whole by an assistant — so it names the options in it, rather than
   * saying "the options are listed below" to a reader who will never see below.
   */
  readonly answer: string;
  /** Rendered above the list. Says who wrote this page and that we are on it. */
  readonly disclosure: string;
  /** Ordered. Position is the ranking. */
  readonly entries: readonly BestEntry[];
  /** The one to pick, and why. Not always us — see the file header. */
  readonly pick: string;
  /** How the list was ordered, so a reader can disagree with the method. */
  readonly method: string;
  readonly faq: readonly { q: string; a: string }[];
}
